import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadBroadcastRecipientsCsv } from '../../../../services/broadcastService';
import { broadcastRecipientsQueryKey } from './useFetchBroadcastRecipients';

interface IUploadRecipientResult {
  isSuccess: boolean;
  message: string;
}

// Helper to extract message from API response (handles both camelCase and PascalCase)
const getApiMessage = (data: any, fallback: string): string => {
  return data?.message || data?.Message || fallback;
};

export const useUploadBroadcastRecipients = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    (formData: FormData) => uploadBroadcastRecipientsCsv(formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [broadcastRecipientsQueryKey] });
      },
    },
  );

  const uploadRecipients = async (file: File): Promise<IUploadRecipientResult> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await mutateAsync(formData);
      const message = getApiMessage(result, result.isSuccess ? 'CSV uploaded successfully.' : 'Upload failed.');
      
      return {
        isSuccess: result.isSuccess,
        message,
      };
    } catch (error: any) {
      const responseData = error?.data || error?.response?.data;
      const errorMessage = getApiMessage(responseData, 'An error occurred while processing the CSV file.');
      
      return {
        isSuccess: false,
        message: errorMessage,
      };
    }
  };

  return {
    uploadRecipients,
    isUploading: isLoading,
  };
};
